import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  useTherapistBookings,
  useBookingDetail,
  useCancelBooking,
  type Booking,
} from '@theraply/api-client';
import { Modal } from '@theraply/ui';
import {
  CalendarIcon,
  ClockIcon,
  VideoCameraIcon,
  XMarkIcon,
  EyeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

type TabKey = 'upcoming' | 'past' | 'cancelled';

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatShortDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function getSessionTypeName(type: Booking['sessionType']): string {
  switch (type) {
    case 'INTAKE':
      return 'Initial Intake';
    case 'REGULAR':
      return 'Regular Session';
    case 'CRISIS':
      return 'Crisis Intervention';
    default:
      return type;
  }
}

function getStatusBadge(status: Booking['status']) {
  switch (status) {
    case 'CONFIRMED':
      return { label: 'Confirmed', className: 'bg-green-100 text-green-800' };
    case 'COMPLETED':
      return { label: 'Completed', className: 'bg-blue-100 text-blue-800' };
    case 'CANCELLED':
      return { label: 'Cancelled', className: 'bg-red-100 text-red-800' };
    case 'PENDING_PAYMENT':
      return { label: 'Pending Payment', className: 'bg-yellow-100 text-yellow-800' };
    default:
      return { label: status, className: 'bg-gray-100 text-gray-800' };
  }
}

function isUpcoming(booking: Booking): boolean {
  if (booking.status !== 'CONFIRMED' && booking.status !== 'PENDING_PAYMENT') return false;
  const now = new Date();
  const bookingDate = new Date(`${booking.slotDate}T${booking.slotTime}`);
  return bookingDate > now;
}

function isPast(booking: Booking): boolean {
  if (booking.status === 'CANCELLED') return false;
  if (booking.status === 'COMPLETED') return true;
  const now = new Date();
  const bookingDate = new Date(`${booking.slotDate}T${booking.slotTime}`);
  return bookingDate <= now;
}

export default function SessionsPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('upcoming');
  const [selectedBookingId, setSelectedBookingId] = useState<number | undefined>();
  const [cancelBookingId, setCancelBookingId] = useState<number | undefined>();
  const [cancelReason, setCancelReason] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const { data, isLoading, error, refetch } = useTherapistBookings();
  const { data: selectedBooking } = useBookingDetail(selectedBookingId);
  const cancelMutation = useCancelBooking();

  const bookings = data?.bookings || [];

  const filteredBookings = useMemo(() => {
    let filtered: Booking[];
    switch (activeTab) {
      case 'upcoming':
        filtered = bookings.filter(isUpcoming);
        filtered.sort((a, b) => {
          const dateA = new Date(`${a.slotDate}T${a.slotTime}`);
          const dateB = new Date(`${b.slotDate}T${b.slotTime}`);
          return dateA.getTime() - dateB.getTime();
        });
        break;
      case 'past':
        filtered = bookings.filter(isPast);
        filtered.sort((a, b) => {
          const dateA = new Date(`${a.slotDate}T${a.slotTime}`);
          const dateB = new Date(`${b.slotDate}T${b.slotTime}`);
          return dateB.getTime() - dateA.getTime();
        });
        break;
      case 'cancelled':
        filtered = bookings.filter((b) => b.status === 'CANCELLED');
        filtered.sort((a, b) => {
          const dateA = new Date(a.cancelledAt || a.updatedAt);
          const dateB = new Date(b.cancelledAt || b.updatedAt);
          return dateB.getTime() - dateA.getTime();
        });
        break;
      default:
        filtered = bookings;
    }
    return filtered;
  }, [bookings, activeTab]);

  const paginatedBookings = filteredBookings.slice(
    page * pageSize,
    (page + 1) * pageSize
  );
  const totalPages = Math.ceil(filteredBookings.length / pageSize);

  const handleCancel = async () => {
    if (!cancelBookingId) return;
    try {
      await cancelMutation.mutateAsync({
        bookingId: cancelBookingId,
        reason: cancelReason || undefined,
      });
      setCancelBookingId(undefined);
      setCancelReason('');
    } catch (err) {
      console.error('Failed to cancel booking:', err);
    }
  };

  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: 'upcoming', label: 'Upcoming', count: bookings.filter(isUpcoming).length },
    { key: 'past', label: 'Past', count: bookings.filter(isPast).length },
    { key: 'cancelled', label: 'Cancelled', count: bookings.filter((b) => b.status === 'CANCELLED').length },
  ];

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading sessions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
          <ExclamationTriangleIcon className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <h3 className="text-red-800 font-semibold mb-2">Error Loading Sessions</h3>
          <p className="text-red-600 text-sm mb-4">
            Failed to load your sessions. Please try again.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Sessions</h1>
        <p className="mt-2 text-sm text-gray-600">
          View and manage your therapy sessions.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setPage(0);
              }}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              <span
                className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeTab === tab.key
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Session List */}
      {paginatedBookings.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CalendarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No {activeTab} sessions
          </h3>
          <p className="text-gray-500 text-sm">
            {activeTab === 'upcoming'
              ? 'You have no upcoming sessions scheduled.'
              : activeTab === 'past'
              ? 'No past sessions found.'
              : 'No cancelled sessions.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {paginatedBookings.map((booking) => {
            const badge = getStatusBadge(booking.status);
            return (
              <div
                key={booking.id}
                className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <VideoCameraIcon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">
                        {booking.consumerName || `Client #${booking.consumerId}`}
                      </h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm text-gray-500 flex items-center gap-1">
                          <CalendarIcon className="h-4 w-4" />
                          {formatShortDate(booking.slotDate)}
                        </span>
                        <span className="text-sm text-gray-500 flex items-center gap-1">
                          <ClockIcon className="h-4 w-4" />
                          {formatTime(booking.slotTime)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
                          {badge.label}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          {getSessionTypeName(booking.sessionType)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatCurrency(booking.amountCents)}
                        </span>
                      </div>
                      {booking.cancellationReason && (
                        <p className="mt-2 text-sm text-red-600">
                          Reason: {booking.cancellationReason}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedBookingId(booking.id)}
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                      title="View details"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    {activeTab === 'upcoming' && booking.status === 'CONFIRMED' && (
                      <>
                        <Link
                          to={`/session/${booking.id}`}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                        >
                          <VideoCameraIcon className="h-4 w-4 mr-1" />
                          Join Video
                        </Link>
                        <button
                          onClick={() => setCancelBookingId(booking.id)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md"
                        >
                          <XMarkIcon className="h-4 w-4 mr-1" />
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between bg-white px-4 py-3 rounded-lg shadow">
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">{page * pageSize + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min((page + 1) * pageSize, filteredBookings.length)}
                </span>{' '}
                of <span className="font-medium">{filteredBookings.length}</span> results
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 text-sm font-medium border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1 text-sm font-medium border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {selectedBookingId && selectedBooking && (
        <Modal
          isOpen={!!selectedBookingId}
          onClose={() => setSelectedBookingId(undefined)}
          title="Session Details"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Client</p>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {selectedBooking.consumerName || `Client #${selectedBooking.consumerId}`}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Status</p>
                <p className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(selectedBooking.status).className}`}>
                    {getStatusBadge(selectedBooking.status).label}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Date</p>
                <p className="mt-1 text-sm text-gray-900">
                  {formatDate(selectedBooking.slotDate)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Time</p>
                <p className="mt-1 text-sm text-gray-900">
                  {formatTime(selectedBooking.slotTime)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Session Type</p>
                <p className="mt-1 text-sm text-gray-900">
                  {getSessionTypeName(selectedBooking.sessionType)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Amount</p>
                <p className="mt-1 text-sm text-gray-900">
                  {formatCurrency(selectedBooking.amountCents)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Your Earnings</p>
                <p className="mt-1 text-sm font-medium text-green-700">
                  {formatCurrency(selectedBooking.therapistAmountCents)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Platform Fee</p>
                <p className="mt-1 text-sm text-gray-500">
                  {formatCurrency(selectedBooking.platformFeeCents)}
                </p>
              </div>
            </div>
            {selectedBooking.notes && (
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Notes</p>
                <p className="mt-1 text-sm text-gray-700">{selectedBooking.notes}</p>
              </div>
            )}
            {selectedBooking.cancellationReason && (
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Cancellation Reason</p>
                <p className="mt-1 text-sm text-red-600">{selectedBooking.cancellationReason}</p>
              </div>
            )}
            <div className="text-xs text-gray-400">
              Created: {new Date(selectedBooking.createdAt).toLocaleString()}
            </div>
          </div>
        </Modal>
      )}

      {/* Cancel Confirmation Modal */}
      {cancelBookingId && (
        <Modal
          isOpen={!!cancelBookingId}
          onClose={() => {
            setCancelBookingId(undefined);
            setCancelReason('');
          }}
          title="Cancel Session"
        >
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    Are you sure you want to cancel this session?
                  </p>
                  <p className="mt-1 text-sm text-yellow-700">
                    The client will be notified and may receive a refund depending on the cancellation policy.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label
                htmlFor="cancel-reason"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Reason for cancellation (optional)
              </label>
              <textarea
                id="cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter a reason..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setCancelBookingId(undefined);
                  setCancelReason('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Keep Session
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Session'}
              </button>
            </div>

            {cancelMutation.isError && (
              <p className="text-sm text-red-600">
                Failed to cancel session. Please try again.
              </p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
